import React, { useMemo } from 'react';
import {
  FiSearch,
  FiCheck,
  FiX,
  FiAlertCircle,
  FiInfo,
  FiChevronDown,
  FiChevronUp,
  FiTrendingUp,
  FiZap,
  FiGlobe,
  FiCpu,
  FiEye,
  FiFileText,
  FiLink,
  FiImage,
  FiType,
} from 'react-icons/fi';
import './SeoPanel.css';

// SEO Score Circle Component
const ScoreCircle = ({ score, size = 80, strokeWidth = 8, label }) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (score / 100) * circumference;

  const getColor = () => {
    if (score >= 70) return '#10B981'; // Green
    if (score >= 40) return '#F59E0B'; // Yellow
    return '#EF4444'; // Red
  };

  const getLabel = () => {
    if (score >= 70) return 'Good';
    if (score >= 40) return 'Needs Work';
    return 'Poor';
  };

  return (
    <div className="seo-score-circle">
      <svg width={size} height={size}>
        <circle
          className="score-circle-bg"
          cx={size / 2}
          cy={size / 2}
          r={radius}
          strokeWidth={strokeWidth}
        />
        <circle
          className="score-circle-progress"
          cx={size / 2}
          cy={size / 2}
          r={radius}
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          style={{ stroke: getColor() }}
        />
      </svg>
      <div className="score-circle-value">
        <span className="score-number" style={{ color: getColor() }}>
          {score}
        </span>
        <span className="score-label">{label || getLabel()}</span>
      </div>
    </div>
  );
};

// Checklist Item Component
const ChecklistItem = ({ status, title, description, priority = 'medium' }) => {
  const getStatusIcon = () => {
    switch (status) {
      case 'pass':
        return <FiCheck className="status-icon pass" />;
      case 'fail':
        return <FiX className="status-icon fail" />;
      case 'warning':
        return <FiAlertCircle className="status-icon warning" />;
      default:
        return <FiInfo className="status-icon info" />;
    }
  };

  return (
    <div className={`seo-checklist-item ${status} priority-${priority}`}>
      {getStatusIcon()}
      <div className="checklist-content">
        <span className="checklist-title">{title}</span>
        {description && <span className="checklist-description">{description}</span>}
      </div>
    </div>
  );
};

// Main SEO Panel Component
const SeoPanel = ({
  title = '',
  content = null,
  excerpt = '',
  focusKeyword = '',
  seoTitle = '',
  seoDescription = '',
  slug = '',
  images = [],
  links = [],
  className = '',
}) => {
  // Calculate all SEO metrics
  const analysis = useMemo(() => {
    const checks = [];
    let seoScore = 0;
    let readabilityScore = 0;
    let totalChecks = 0;
    let passedChecks = 0;

    // Extract text content from TipTap JSON or string
    const getTextContent = (contentData) => {
      if (!contentData) return '';
      if (typeof contentData === 'string') return contentData;

      const extractText = (node) => {
        if (!node) return '';
        if (typeof node === 'string') return node;
        if (node.text) return node.text;
        if (node.content && Array.isArray(node.content)) {
          return node.content.map(extractText).join(' ');
        }
        return '';
      };

      return extractText(contentData);
    };

    const textContent = getTextContent(content);
    const words = textContent.split(/\s+/).filter(w => w.length > 0);
    const wordCount = words.length;
    const sentences = textContent.split(/[.!?]+/).filter(s => s.trim().length > 0);
    const paragraphs = textContent.split(/\n\n+/).filter(p => p.trim().length > 0);

    // =========================================================================
    // TITLE CHECKS
    // =========================================================================

    // Title exists
    totalChecks++;
    if (title && title.trim().length > 0) {
      passedChecks++;
      checks.push({
        id: 'title-exists',
        status: 'pass',
        title: 'Title is set',
        description: `Title: "${title}"`,
        category: 'title',
        priority: 'high',
      });
    } else {
      checks.push({
        id: 'title-exists',
        status: 'fail',
        title: 'No title found',
        description: 'Add a title to your content',
        category: 'title',
        priority: 'high',
      });
    }

    // Title length (50-60 chars ideal for SEO title)
    totalChecks++;
    const titleToCheck = seoTitle || title;
    const titleLength = titleToCheck?.length || 0;
    if (titleLength >= 50 && titleLength <= 60) {
      passedChecks++;
      checks.push({
        id: 'title-length',
        status: 'pass',
        title: 'SEO title length is optimal',
        description: `${titleLength} characters (50-60 recommended)`,
        category: 'title',
        priority: 'high',
      });
    } else if (titleLength > 0 && titleLength < 50) {
      checks.push({
        id: 'title-length',
        status: 'warning',
        title: 'SEO title is a bit short',
        description: `${titleLength} characters (50-60 recommended)`,
        category: 'title',
        priority: 'high',
      });
    } else if (titleLength > 60) {
      checks.push({
        id: 'title-length',
        status: 'warning',
        title: 'SEO title is too long',
        description: `${titleLength} characters (50-60 recommended). It may be truncated in search results.`,
        category: 'title',
        priority: 'high',
      });
    } else {
      checks.push({
        id: 'title-length',
        status: 'fail',
        title: 'Set an SEO title',
        description: 'SEO title should be 50-60 characters',
        category: 'title',
        priority: 'high',
      });
    }

    // Focus keyword in title
    totalChecks++;
    if (focusKeyword && title?.toLowerCase().includes(focusKeyword.toLowerCase())) {
      passedChecks++;
      checks.push({
        id: 'keyword-in-title',
        status: 'pass',
        title: 'Focus keyword in title',
        description: `"${focusKeyword}" appears in the title`,
        category: 'keyword',
        priority: 'high',
      });
    } else if (focusKeyword) {
      checks.push({
        id: 'keyword-in-title',
        status: 'fail',
        title: 'Focus keyword not in title',
        description: `Add "${focusKeyword}" to your title`,
        category: 'keyword',
        priority: 'high',
      });
    }

    // =========================================================================
    // META DESCRIPTION CHECKS
    // =========================================================================

    totalChecks++;
    const descLength = seoDescription?.length || 0;
    if (descLength >= 140 && descLength <= 160) {
      passedChecks++;
      checks.push({
        id: 'meta-description-length',
        status: 'pass',
        title: 'Meta description length is optimal',
        description: `${descLength} characters (140-160 recommended)`,
        category: 'meta',
        priority: 'high',
      });
    } else if (descLength > 0 && descLength < 140) {
      checks.push({
        id: 'meta-description-length',
        status: 'warning',
        title: 'Meta description is short',
        description: `${descLength} characters (140-160 recommended)`,
        category: 'meta',
        priority: 'high',
      });
    } else if (descLength > 160) {
      checks.push({
        id: 'meta-description-length',
        status: 'warning',
        title: 'Meta description is too long',
        description: `${descLength} characters (140-160 recommended). It will be truncated.`,
        category: 'meta',
        priority: 'high',
      });
    } else {
      checks.push({
        id: 'meta-description-length',
        status: 'fail',
        title: 'No meta description',
        description: 'Add a meta description (140-160 characters)',
        category: 'meta',
        priority: 'high',
      });
    }

    // Focus keyword in meta description
    totalChecks++;
    if (focusKeyword && seoDescription?.toLowerCase().includes(focusKeyword.toLowerCase())) {
      passedChecks++;
      checks.push({
        id: 'keyword-in-description',
        status: 'pass',
        title: 'Focus keyword in meta description',
        description: `"${focusKeyword}" appears in the meta description`,
        category: 'keyword',
        priority: 'medium',
      });
    } else if (focusKeyword && seoDescription) {
      checks.push({
        id: 'keyword-in-description',
        status: 'warning',
        title: 'Focus keyword not in meta description',
        description: `Consider adding "${focusKeyword}" to your meta description`,
        category: 'keyword',
        priority: 'medium',
      });
    }

    // =========================================================================
    // CONTENT CHECKS
    // =========================================================================

    // Word count
    totalChecks++;
    if (wordCount >= 300) {
      passedChecks++;
      checks.push({
        id: 'word-count',
        status: 'pass',
        title: 'Content length is good',
        description: `${wordCount} words (300+ recommended)`,
        category: 'content',
        priority: 'high',
      });
    } else if (wordCount >= 150) {
      checks.push({
        id: 'word-count',
        status: 'warning',
        title: 'Content is a bit short',
        description: `${wordCount} words (300+ recommended for SEO)`,
        category: 'content',
        priority: 'high',
      });
    } else {
      checks.push({
        id: 'word-count',
        status: 'fail',
        title: 'Content is too short',
        description: `${wordCount} words (300+ recommended for SEO)`,
        category: 'content',
        priority: 'high',
      });
    }

    // Focus keyword in content
    totalChecks++;
    if (focusKeyword) {
      const keywordRegex = new RegExp(focusKeyword, 'gi');
      const keywordMatches = textContent.match(keywordRegex) || [];
      const keywordDensity = wordCount > 0 ? (keywordMatches.length / wordCount * 100) : 0;

      if (keywordMatches.length >= 2 && keywordDensity <= 3) {
        passedChecks++;
        checks.push({
          id: 'keyword-in-content',
          status: 'pass',
          title: 'Focus keyword appears in content',
          description: `"${focusKeyword}" appears ${keywordMatches.length} times (${keywordDensity.toFixed(1)}% density)`,
          category: 'keyword',
          priority: 'high',
        });
      } else if (keywordMatches.length > 0 && keywordDensity > 3) {
        checks.push({
          id: 'keyword-in-content',
          status: 'warning',
          title: 'Keyword density is too high',
          description: `"${focusKeyword}" appears ${keywordMatches.length} times (${keywordDensity.toFixed(1)}% density). Reduce to avoid keyword stuffing.`,
          category: 'keyword',
          priority: 'high',
        });
      } else if (keywordMatches.length === 1) {
        checks.push({
          id: 'keyword-in-content',
          status: 'warning',
          title: 'Focus keyword appears only once',
          description: 'Use your focus keyword a few more times naturally',
          category: 'keyword',
          priority: 'high',
        });
      } else {
        checks.push({
          id: 'keyword-in-content',
          status: 'fail',
          title: 'Focus keyword not in content',
          description: `Add "${focusKeyword}" to your content`,
          category: 'keyword',
          priority: 'high',
        });
      }
    }

    // Focus keyword in first paragraph
    totalChecks++;
    if (focusKeyword && paragraphs.length > 0) {
      if (paragraphs[0].toLowerCase().includes(focusKeyword.toLowerCase())) {
        passedChecks++;
        checks.push({
          id: 'keyword-in-intro',
          status: 'pass',
          title: 'Focus keyword in introduction',
          description: 'Great! The keyword appears in the first paragraph',
          category: 'keyword',
          priority: 'medium',
        });
      } else {
        checks.push({
          id: 'keyword-in-intro',
          status: 'warning',
          title: 'Focus keyword not in introduction',
          description: 'Add your focus keyword to the first paragraph',
          category: 'keyword',
          priority: 'medium',
        });
      }
    }

    // =========================================================================
    // SLUG CHECK
    // =========================================================================

    totalChecks++;
    if (slug && slug.length > 0) {
      if (focusKeyword && slug.toLowerCase().includes(focusKeyword.toLowerCase().replace(/\s+/g, '-'))) {
        passedChecks++;
        checks.push({
          id: 'keyword-in-slug',
          status: 'pass',
          title: 'Focus keyword in URL slug',
          description: `Great! "/${slug}" contains your keyword`,
          category: 'url',
          priority: 'medium',
        });
      } else if (focusKeyword) {
        checks.push({
          id: 'keyword-in-slug',
          status: 'warning',
          title: 'Focus keyword not in URL',
          description: 'Consider adding your keyword to the URL slug',
          category: 'url',
          priority: 'medium',
        });
      } else {
        passedChecks++;
        checks.push({
          id: 'slug-exists',
          status: 'pass',
          title: 'URL slug is set',
          description: `/${slug}`,
          category: 'url',
          priority: 'medium',
        });
      }
    } else {
      checks.push({
        id: 'slug-exists',
        status: 'fail',
        title: 'No URL slug',
        description: 'Set a URL slug for your content',
        category: 'url',
        priority: 'medium',
      });
    }

    // =========================================================================
    // READABILITY CHECKS
    // =========================================================================

    // Average sentence length
    const avgSentenceLength = sentences.length > 0 ? wordCount / sentences.length : 0;
    totalChecks++;
    if (avgSentenceLength > 0 && avgSentenceLength <= 20) {
      passedChecks++;
      readabilityScore += 25;
      checks.push({
        id: 'sentence-length',
        status: 'pass',
        title: 'Sentence length is good',
        description: `Average ${avgSentenceLength.toFixed(1)} words per sentence`,
        category: 'readability',
        priority: 'low',
      });
    } else if (avgSentenceLength > 20) {
      readabilityScore += 10;
      checks.push({
        id: 'sentence-length',
        status: 'warning',
        title: 'Sentences are too long',
        description: `Average ${avgSentenceLength.toFixed(1)} words per sentence. Try to keep it under 20.`,
        category: 'readability',
        priority: 'low',
      });
    }

    // Paragraph length
    const longParagraphs = paragraphs.filter(p => p.split(/\s+/).length > 150);
    totalChecks++;
    if (longParagraphs.length === 0 && paragraphs.length > 0) {
      passedChecks++;
      readabilityScore += 25;
      checks.push({
        id: 'paragraph-length',
        status: 'pass',
        title: 'Paragraph length is good',
        description: 'All paragraphs are a reasonable length',
        category: 'readability',
        priority: 'low',
      });
    } else if (longParagraphs.length > 0) {
      readabilityScore += 10;
      checks.push({
        id: 'paragraph-length',
        status: 'warning',
        title: 'Some paragraphs are too long',
        description: `${longParagraphs.length} paragraph(s) have more than 150 words. Break them up.`,
        category: 'readability',
        priority: 'low',
      });
    }

    // Subheadings check (from TipTap content)
    const countHeadings = (contentData) => {
      if (!contentData || typeof contentData === 'string') return 0;
      let count = 0;
      const traverse = (node) => {
        if (node.type === 'heading') count++;
        if (node.content) node.content.forEach(traverse);
      };
      traverse(contentData);
      return count;
    };

    const headingCount = countHeadings(content);
    totalChecks++;
    if (headingCount >= 2 || wordCount < 300) {
      passedChecks++;
      readabilityScore += 25;
      checks.push({
        id: 'subheadings',
        status: 'pass',
        title: 'Good use of subheadings',
        description: `${headingCount} heading(s) found`,
        category: 'structure',
        priority: 'medium',
      });
    } else if (headingCount === 1) {
      readabilityScore += 15;
      checks.push({
        id: 'subheadings',
        status: 'warning',
        title: 'Add more subheadings',
        description: 'Use H2 and H3 headings to structure your content',
        category: 'structure',
        priority: 'medium',
      });
    } else {
      checks.push({
        id: 'subheadings',
        status: 'fail',
        title: 'No subheadings found',
        description: 'Add headings (H2, H3) to structure your content',
        category: 'structure',
        priority: 'medium',
      });
    }

    // =========================================================================
    // CALCULATE SCORES
    // =========================================================================

    // SEO Score based on passed checks
    seoScore = totalChecks > 0 ? Math.round((passedChecks / totalChecks) * 100) : 0;

    // Readability score (simplified Flesch-like)
    if (wordCount > 50) {
      const avgWordLength = textContent.replace(/\s+/g, '').length / wordCount;
      const fleschLike = 100 - (avgSentenceLength * 1.5) - (avgWordLength * 10);
      readabilityScore = Math.max(0, Math.min(100, Math.round(fleschLike + readabilityScore)));
    } else {
      readabilityScore = 50; // Default for short content
    }

    // Group checks by category
    const groupedChecks = checks.reduce((acc, check) => {
      if (!acc[check.category]) {
        acc[check.category] = [];
      }
      acc[check.category].push(check);
      return acc;
    }, {});

    // Determine friendly status
    const googleFriendly = seoScore >= 60 && passedChecks >= totalChecks * 0.6;
    const aiFriendly = wordCount >= 200 && headingCount >= 1 && sentences.length >= 3;

    return {
      seoScore,
      readabilityScore,
      totalChecks,
      passedChecks,
      failedChecks: totalChecks - passedChecks,
      checks,
      groupedChecks,
      googleFriendly,
      aiFriendly,
      wordCount,
      sentenceCount: sentences.length,
      paragraphCount: paragraphs.length,
      headingCount,
    };
  }, [title, content, excerpt, focusKeyword, seoTitle, seoDescription, slug]);

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'title': return FiType;
      case 'meta': return FiFileText;
      case 'keyword': return FiSearch;
      case 'content': return FiFileText;
      case 'url': return FiLink;
      case 'readability': return FiEye;
      case 'structure': return FiGlobe;
      default: return FiInfo;
    }
  };

  const getCategoryLabel = (category) => {
    switch (category) {
      case 'title': return 'Title';
      case 'meta': return 'Meta';
      case 'keyword': return 'Keyword';
      case 'content': return 'Content';
      case 'url': return 'URL';
      case 'readability': return 'Readability';
      case 'structure': return 'Structure';
      default: return 'Other';
    }
  };

  return (
    <div className={`seo-panel ${className}`}>
      {/* Score Overview */}
      <div className="seo-panel-scores">
        <div className="seo-score-section">
          <ScoreCircle score={analysis.seoScore} label="SEO" />
        </div>
        <div className="seo-score-section">
          <ScoreCircle score={analysis.readabilityScore} size={60} strokeWidth={6} label="Readability" />
        </div>
      </div>

      {/* Status Indicators */}
      <div className="seo-status-row">
        <div className={`seo-status-badge ${analysis.googleFriendly ? 'good' : 'poor'}`}>
          <FiGlobe size={14} />
          <span>Google {analysis.googleFriendly ? 'Friendly' : 'Needs Work'}</span>
        </div>
        <div className={`seo-status-badge ${analysis.aiFriendly ? 'good' : 'poor'}`}>
          <FiCpu size={14} />
          <span>AI {analysis.aiFriendly ? 'Friendly' : 'Needs Work'}</span>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="seo-quick-stats">
        <div className="seo-stat">
          <span className="stat-value">{analysis.wordCount}</span>
          <span className="stat-label">Words</span>
        </div>
        <div className="seo-stat">
          <span className="stat-value">{analysis.headingCount}</span>
          <span className="stat-label">Headings</span>
        </div>
        <div className="seo-stat">
          <span className="stat-value">{analysis.passedChecks}/{analysis.totalChecks}</span>
          <span className="stat-label">Checks</span>
        </div>
      </div>

      {/* Checklist by Category */}
      <div className="seo-checklist">
        {Object.entries(analysis.groupedChecks).map(([category, items]) => {
          const Icon = getCategoryIcon(category);
          const passCount = items.filter(i => i.status === 'pass').length;
          return (
            <div key={category} className="seo-checklist-group">
              <div className="checklist-group-header">
                <Icon size={14} />
                <span>{getCategoryLabel(category)}</span>
                <span className="checklist-count">{passCount}/{items.length}</span>
              </div>
              <div className="checklist-group-items">
                {items.map(item => (
                  <ChecklistItem key={item.id} {...item} />
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Focus Keyword Reminder */}
      {!focusKeyword && (
        <div className="seo-keyword-reminder">
          <FiAlertCircle size={16} />
          <span>Set a focus keyword for better SEO analysis</span>
        </div>
      )}
    </div>
  );
};

export default SeoPanel;
